import React from 'react';
import * as THREE from "three";
import EventEmitter from 'eventemitter3';
import JSZip from 'jszip';
import ARTracker from './ARTracker';
import Callout from './Callout';
import Text3D from './Text3D';
import Scenario from './scenarioElements/Scenario';
import Utils from './Utilities';
import {GroupsManager} from './GroupsManager';
import DefaultSceneManager from './DefaultSceneManager';
import ScopeCamera from './Camera';
import AudioSource from './AudioSource';
import MaterialAsset from './MaterialAsset';
import Engine from './Engine';
import ProjectSaver from './ProjectSaver';
import MediaPanel from './MediaPanel';
import RbopLoader from './RbopLoader';
import ErrorHandler, { assert, ErrorCategory, ErrorSeverity } from './utilities/ErrorHandling';
import QuizManager from './QuizManager';
import AssetsManager from './AssetsManager';
import MathUtils from './utilities/MathUtils';
import SceneObjectUtils, { TwoKeySubObjectLookupDatabase } from './utilities/SceneObjectUtils';
import TransformUtils from './utilities/TransformUtils';
import LegacyProjectLoader from './LegacyProjectLoader';
import ScenarioHealthUtils from './utilities/ScenarioHealthUtils';
import { MetadataManager } from './MetadataManager';
import AssetUtility from './AssetUtility';
import ScopeLight from './Light';
import {Timeline} from './timeline/Timeline';
import APIUtility from './APIUtility';
import CubemapUtils from './utilities/CubemapUtils';
import sleep from './utilities/sleep';
import SceneCameraController from './SceneCameraController';
import { addURLParam } from './utilities/Misc';
import MessagePrompter from '../components/prompts/MessagePrompter';
import { refreshExportsFromServer } from '../components/filebrowser/FileManager';
import { CustomEventTypes, LightTypes, SimulationStates, defaultTriggerObject, SaveModificationTypes, WorldUnitScales } from '../globals';
import Store, { StoreActions } from '../state/store';
import { chooseSaveFile } from '../components/welcome/ProjectSaveSelector';
import AssetsLibraryUtilities from '../components/filebrowser/AssetsLibraryUtilities';
import {Trigger} from "../interfaces/Trigger";
import { BuiltInCubeMaps, IMetaDataObject } from '../interfaces/Misc';
import { PivotSpace } from '../interfaces/Pivot';
import { IProjectInfo } from '../interfaces/ScenarioElements';
import { IModifiedChildrenData, INodeSaveData, IProjectSaveData, IReorderedChildIdentifier } from '../interfaces/ProjectSave';
import {ProjectLoadingEvents, ProjectLoadingStates} from "../interfaces/enums";
import { AssetCollections } from '../interfaces/AssetLibrary';
import StandbyOverlay from '../components/layout/StandbyOverlay';

interface ILoadedObjectData
{
	object: THREE.Object3D;
	parent?: string;
	parentSaveRoot?: string;
	nodeIndex?: number;
	primitiveIndex?: number;
}

interface ILoadedLinkedObjectData
{
	object: THREE.Object3D;
	defaultParent?: string;
	defaultParentSaveRoot?: string;
	nodeIndex?: number;
	primitiveIndex?: number;
	sceneGuid: string;
}

type saveJSONBlob = {saveJSON: string, uiDataJSON: string | undefined, timelineDataJSON: string | undefined, sceneDatas: string[]};

export default class ProjectLoader extends EventEmitter
{
	set state(value: ProjectLoadingStates)
	{
		this.emit(ProjectLoadingEvents.STATE, value);
		this.emit(ProjectLoadingEvents.LOADING_PERCENT, 0);
	}

	protected version = 0;
	private sceneSaveRoots: Map<string, THREE.Object3D[]> = new Map();
	private loadedObjects: Map<string, ILoadedObjectData> = new Map();
	private loadedLinkedObjects: ILoadedLinkedObjectData[] = [];
	private childReorderMap: Map<THREE.Object3D, IReorderedChildIdentifier[]> = new Map();
	private subObjectsLookUp: Map<string, TwoKeySubObjectLookupDatabase> = new Map();
	private extrudedTextObjects: Text3D[] = [];
	private interactiveCameras: Map<string, ScopeCamera> = new Map();

	static async isZip(blob: Blob) 
	{
		const header = new Uint8Array((await blob.arrayBuffer()).slice(0, 4));
		if(header.length < 4) return false;
		return header[0] === 0x50 && header[1] === 0x4b && header[2] === 0x03 && header[3] === 0x04;
	}

	static async extractZip(zip: JSZip) 
	{
		let saveJsonFileSize = 0;
		let sceneDatas: string[] = [];

		const saveJSON = await zip.file("save.json")?.async("string");
		saveJsonFileSize = saveJSON?.length ?? 0;

		const uiDataJSON = await zip.file("uiData.json")?.async("string");
		uiDataJSON && (saveJsonFileSize += uiDataJSON.length);

		const timelineDataJSON = await zip.file("timelineData.json")?.async("string");
		timelineDataJSON && (saveJsonFileSize += timelineDataJSON.length);

		const content: Promise<string>[] = [];
		const folder = Object.keys(zip.files).filter((key) => key.startsWith("scenes/") && key.endsWith(".json"));
					
		folder.forEach((filename) => 
		{
			// If you want to do something with the file, like extracting it:
			const file = zip.file(filename);
			file && content.push(file.async('string'));
		});
		// this could be on demand loading <<< can't actually do this because the loading process requires all scenes and their nodes to be loaded before continuing
		await Promise.all(content).then((sceneData) => 
		{
			sceneDatas = sceneData;
		});

		sceneDatas.forEach((sceneData) => saveJsonFileSize += sceneData.length);
		Store.dispatch(StoreActions.scenario.setProjectSize(saveJsonFileSize));

		return {saveJSON, uiDataJSON, timelineDataJSON, sceneDatas};
	}

	static async extractBlob(blob: Blob)
	{
		const isZip = await ProjectLoader.isZip(blob);
		if (isZip) 
		{
			const zip = await JSZip.loadAsync(blob);
			return  await ProjectLoader.extractZip(zip);
		}
		else
		{
			const saveJSON =  await blob.text();
			Store.dispatch(StoreActions.scenario.setProjectSize(saveJSON.length));
			return  { saveJSON, uiDataJSON: undefined, timelineDataJSON: undefined, sceneDatas: []};
		}
	}

	async load(projectFileURL : string, projectId: string)
	{
		console.log("Loading project " + projectFileURL);
		
		return new Promise<void>((resolve, reject) =>
		{
			const fetchProjectPermissions = async () =>
			{
				try 
				{
					const data = await APIUtility.openProject(projectId);
					const projectInfo: IProjectInfo = data?.project;
					Store.dispatch(StoreActions.scenario.setPermissions({
						canView: projectInfo?.permissions?.canView?.value?? false,
						canUpdate: projectInfo?.permissions?.canUpdate?.value?? false,
						canDelete: projectInfo?.permissions?.canDelete?.value?? false,
						canReview: projectInfo?.permissions?.canReview?.value?? false,
						canManage: projectInfo?.permissions?.canManage?.value?? false,
					}));
				}
				catch (error)
				{
					console.error(error);
				}
			}
			
			fetchProjectPermissions();
			addURLParam("projectid", projectId);
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onProjectLoadStart));
			Store.dispatch(StoreActions.application.setSimulationState(SimulationStates.Stopped));
			Engine.getInstance().clearProjectData();
		
			this.state = ProjectLoadingStates.DOWNLOADING_PROJECT_FILE;

			this.readProjectFile(projectFileURL).then(async (blob) =>
			{
				try 
				{
					const { saveJSON, uiDataJSON, timelineDataJSON, sceneDatas } =  await ProjectLoader.extractBlob(blob);
				
					Store.dispatch(StoreActions.files.registerExports([]));
					Store.dispatch(StoreActions.files.registerImports([]));
					Store.dispatch(StoreActions.history.clearDirtyAction());
					refreshExportsFromServer();

					if(!saveJSON) throw new Error("Failed to load project file");

					// detetct zip file
					const projectData = JSON.parse(saveJSON);
					if (projectData.SiemensRBOP)
					{
						const loader = new RbopLoader(projectData);
						await sleep();
						await loader.load();
						this.centerInteractiveCameraOnContent();
						StandbyOverlay.stopStandbyMode();
					}
					else if (projectData.version)
					{
						this.version = projectData.version;
						await sleep();
						await this.loadProjectData(projectData, uiDataJSON, timelineDataJSON, sceneDatas);
						
						StandbyOverlay.stopStandbyMode();
						window.dispatchEvent(new Event('resize'));
						document.dispatchEvent(new CustomEvent(CustomEventTypes.onProjectLoadEnd));
						ScenarioHealthUtils.FullHealthCheck();
						resolve();	
					}
					else
					{
						this.state = ProjectLoadingStates.DOWNLOADING_ASSETS;
						await sleep();
						const legacyLoader = new LegacyProjectLoader();
						await legacyLoader.loadProjectData(projectData, projectId);
						StandbyOverlay.stopStandbyMode();
						window.dispatchEvent(new Event('resize'));
						document.dispatchEvent(new CustomEvent(CustomEventTypes.onProjectLoadEnd));
						resolve();
					}
				}
				catch (error)
				{
					StandbyOverlay.stopStandbyMode();
					ProjectLoader.projectLoadFailed(projectId, error);
					resolve();
				}
			}, (error) =>
			{
				StandbyOverlay.stopStandbyMode();
				ProjectLoader.projectLoadFailed(projectId, error);
				resolve();
			});
		});
	}


	centerInteractiveCameraOnContent = () => 
	{
		const steps = Scenario.getSteps(false);
		const defaultScene = Scenario.currentSequence()?.defaultStep.scene;
		const lastContentStepIndex = steps.findIndex(step => step.name === "Final Step") - 1;
		if (defaultScene && lastContentStepIndex > 0) 
		{
			const lastContentStep = steps[lastContentStepIndex];
			const sceneCamera = ScopeCamera.getInteractiveModeCamera(defaultScene);
			const interactiveCamera = sceneCamera as ScopeCamera;
			const objects = Engine.getInstance().getSelectionRoot(lastContentStep.sceneId).children.filter(child => child.type === "Mesh");
			const cameraPosition = SceneCameraController.focusOnObjects(interactiveCamera.currentCamera, objects);
			const quaternion = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().lookAt(cameraPosition.position, cameraPosition.anchor, new THREE.Vector3(0, 1, 0)));
			const euler = new THREE.Euler().setFromQuaternion(quaternion);
			DefaultSceneManager.getInstance().syncNativeObjectVectorProperty([interactiveCamera], 'position', [cameraPosition.position]);
			DefaultSceneManager.getInstance().syncNativeObjectVectorProperty([interactiveCamera], 'rotation', [euler]);
			interactiveCamera.position.copy(cameraPosition.position);
			interactiveCamera.rotation.copy(euler);
			ProjectSaver.getInstance().markModifiedObject(interactiveCamera, SaveModificationTypes.Position);
			ProjectSaver.getInstance().markModifiedObject(interactiveCamera, SaveModificationTypes.Rotation);
			const controller = Engine.getInstance().getCameraController();
			if (controller)
			{
				const sceneViewCamera = controller.camera;
				if (sceneViewCamera)
				{
					sceneViewCamera.position.copy(cameraPosition.position);
					sceneViewCamera.rotation.copy(euler);
					controller.getAnchor().copy(cameraPosition.anchor);
				}
			}
			document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectPositionChanged));
		}
	}

	loadProjectData(projectData: IProjectSaveData, uiData?: string, timelineData?: string, sceneDatas?: string[])
	{
		return new Promise<void>((resolve, reject) =>
		{
			try
			{
				const rawAssetPromises: Promise<any>[] = [];
				const modelLoadPromises: Promise<void>[] = [];

				let completedPromises: number = 0;

				const wrapPromise = async <T, >(promise: Promise<T>): Promise<T> => 
				{
					const result = await promise;
					completedPromises++;
					this.emit(ProjectLoadingEvents.LOADING_PERCENT, completedPromises / (rawAssetPromises.length + projectData.modelAssets.length))
					return result;
				};

				const modelLoadProgress = (id: string, url: string, name: string, progress: number) =>
				{
					const completed = completedPromises + progress; // Progress is a decimal between 0-1
					const percent = completed / (rawAssetPromises.length + projectData.modelAssets.length);
					this.emit(ProjectLoadingEvents.LOADING_PERCENT, percent)
				};

				for (const textureAssetId of projectData.materialData.textures)
				{
					const loadPromise = wrapPromise(MaterialAsset.loadTexture(textureAssetId));
					if (loadPromise) rawAssetPromises.push(loadPromise);
				}
				for (const videoAssetId of projectData.materialData.videos)
				{
					const loadPromise = wrapPromise(MaterialAsset.loadVideoTexture(videoAssetId));
					if (loadPromise) rawAssetPromises.push(loadPromise);
				}
				for (const audioAssetId of projectData.audioAssets)
				{
					const loadPromise = wrapPromise(AudioSource.loadAudioClip(audioAssetId));
					if (loadPromise) rawAssetPromises.push(loadPromise);
				}

				if(projectData.envMap && !CubemapUtils.getCubemap(projectData.envMap))
				{
					const loadPromise = wrapPromise(CubemapUtils.loadCubemap(projectData.envMap));
					if (loadPromise) rawAssetPromises.push(loadPromise);
				}

				this.state = ProjectLoadingStates.DOWNLOADING_ASSETS;

				// here we can look for any referenced assets that are missing from the author's library and add their data to the library
				Promise.allSettled(projectData.referencedAssets.map(async (assetId) => 
				{
					// no asset id, can't retrieve any data for it; skip it
					if(!assetId)
					{
						return; // ensure promise termination
					}

					// asset was found in the library already; skip it
					if(AssetsManager.getAssetInfo(assetId)) 
					{
						return; // ensure promise termination
					}

					// get asset info from the server
					const response = await APIUtility.getAssetInfo(assetId);
					if(!response || response.status !== 200 || !Object.keys(response.data).length)
					{
						const error = new Error(`Failed to get asset info for asset ${assetId}`);
						console.error(error);
						throw error; // because we use `allSettled` we can reject without breaking project load here
					}

					// add to the library - but in a location that's not normally visible to the user in the asset library window
					Store.dispatch(StoreActions.assets.addAssetToLibrary({ collection: AssetCollections.Foreign, assetId, assetInfo: { ...response.data, shared: true } }));
					AssetsManager.setPropsForAsset(assetId, AssetsManager.getPropsForAsset(assetId)); // register props as well; required for e.g. asset preview
				})).then(() =>
				{
					AssetsLibraryUtilities.refresh();
					
					//console.log("loading raw assets");
					// now we download the assets as needed
					Promise.all(rawAssetPromises).then(() =>
					{
						//console.log("creating materials");
						for (const material of projectData.materialData.materials) new MaterialAsset(material);

						for (const modelAssetId of projectData.modelAssets)
						{
							const url = AssetUtility.getProcessedAssetURL(modelAssetId);
							const assetName = AssetUtility.getAssetName(modelAssetId)!;
							const promise = wrapPromise(Engine.getInstance().prepareModel(modelAssetId, url, assetName, undefined, false, false, modelLoadProgress));
							modelLoadPromises.push(promise);
						}

						Promise.all(modelLoadPromises).then(() =>
						{
							this.state = ProjectLoadingStates.LOADING_PROJECT;
							setTimeout(() =>
							{
								this.onAssetsLoaded(projectData, uiData, timelineData, sceneDatas).then(() =>
								{
									resolve();
								},
								(error) =>
								{
									reject(error);
								});
							}, 0);
						},
						(error) =>
						{
							reject(error);
						});
					},
					(error) =>
					{
						reject(error);
					})
				});
			}
			catch (error)
			{
				reject(error);
			}
		});
	}
	
	onAssetsLoaded(projectData: IProjectSaveData, uiData?: string, timelineData?: string, sceneDatas?: string[])
	{
		return new Promise<void>((resolve, reject) =>
		{
			try
			{
				GroupsManager.load(projectData.groupsData);
				const hasSceneData = projectData.scenes?.length > 0;

				if(!hasSceneData) 
				{
					const _sceneData = sceneDatas;
					if(!_sceneData) throw new Error("No scene data found in project file");
					projectData.scenes = projectData.scenes || [];
					for (const sceneDataType of _sceneData)
					{
						const sceneData = JSON.parse(sceneDataType as string);
						projectData.scenes.push(sceneData);
					}
					projectData.scenes.sort((scene) => (scene.isDefaultScene ? -1 : 1))
				}
				
				for (const sceneData of projectData.scenes)
				{
					this.sceneSaveRoots.set(sceneData.guid, []);
					Engine.getInstance().createScene({ existingGuid: sceneData.guid });
					const scene = Engine.getInstance().getScene(sceneData.guid)!;
					if (sceneData.isDefaultScene)
					{
						DefaultSceneManager.getInstance().onDefaultSceneLoaded(scene);
					}
					else if (sceneData.defaultScene)
					{
						DefaultSceneManager.getInstance().onLinkedSceneLoaded(scene, sceneData.defaultScene);
					}

					if (sceneData.childOrder) this.childReorderMap.set(Engine.getInstance().getSelectionRoot(sceneData.guid), sceneData.childOrder);
					for (const node of sceneData.nodes) this.loadNode(node, sceneData.guid);
				}

				//console.timeEnd("Load scenes");

				//console.time("Load nodes")
				this.onNodesLoaded(projectData);
				//console.timeEnd("Load nodes")

				assert(Scenario.current(), "Scenario is supposed to be loaded here but it is not");
				if(!Scenario.current()) throw new Error("Scenario is supposed to be loaded here but it is not");

				Store.dispatch(StoreActions.preferences.setProjectSettings({ environmentMapId: projectData.envMap as BuiltInCubeMaps }));
				const worldUnits = Store.getState().preferencesModule.editorPreferences.unitDisplay;
				Engine.getInstance().setDisplayUnitScale(worldUnits ?? WorldUnitScales.Meters);
				Timeline.loadTimelines(timelineData ? JSON.parse(timelineData) : projectData.timelineData);

				// turns out that timelines MUST be loaded BEFORE scenarios, while legacy ui MUST be loaded AFTER scenarios
				// some day we should fix this. probably best to use a step-timelins association in redux map like we do for visage ui and never rely on hard references
				// should help with decoupling the code as well
				assert(Scenario.current(), "Scenario is supposed to be loaded here but it is not");
				if(!Scenario.current()) throw new Error("Scenario is supposed to be loaded here but it is not");
				Scenario.current()!.load(projectData.sequenceData, projectData.trackersData);

				projectData.uiData = uiData ? JSON.parse(uiData) : projectData.uiData
				Store.dispatch(StoreActions.ui.loadUIData(projectData.uiData));
				Store.dispatch(StoreActions.visage.loadUIData(projectData.visageData ?? projectData.uiData.newStepUIData)); // projectData.uiData.newStepUIData is the old save format
				Store.dispatch(StoreActions.ui.loadSlideMasterData({ ui: projectData.slideMasterData }));
				ARTracker.load(projectData.trackersData);
				Store.dispatch(StoreActions.checklist.loadChecklist(projectData.checklistData));
				Store.dispatch(StoreActions.iot.loadIOTData(projectData.iotData));
				Store.dispatch(StoreActions.checklist.setQuizSettings(projectData.quizSettings));
				QuizManager.loadQuizObjects();
				Store.dispatch(StoreActions.scenario.loadStepConditions(projectData.stepConditions));
				Store.dispatch(StoreActions.partsCatalog.loadCatalogs(projectData.partsCatalog));
				Store.dispatch(StoreActions.attachments.loadAttachmentsData(projectData.attachmentsData));

				//backwards comp fix; CPS could find itself linked to a default state that is not the initial sequence's default state. we must link it to the correct default state
				const initialSequence = Scenario.initialSequence();
				assert(initialSequence, "Initial sequence is supposed to be loaded here but it is not");
				if(!initialSequence) throw new Error("Initial sequence is supposed to be loaded here but it is not");

				const trackerStep = initialSequence.getTrackersStep()!;
				if(trackerStep.scene.userData.defaultSceneGuid !== initialSequence.defaultStep.sceneId)
				{
					DefaultSceneManager.getInstance().onSceneMoved(trackerStep.scene, initialSequence.defaultStep.scene, false);
				}
				//backwards fix; CPS should never have legacy ui data
				if(Store.getState().uiModule.ui[trackerStep.id])
				{
					Store.dispatch(StoreActions.ui.removeUIFromStep(trackerStep.id));
				}

				AssetsManager.scanAssetsInProject();
				AssetsLibraryUtilities.refresh();

				MaterialAsset.waitForVideoLoadPromises().then(() =>
				{
					resolve();
				});
			}
			catch (error)
			{
				reject(error);
			}
		});
	}

	onNodesLoaded(projectData: IProjectSaveData)
	{
		for (const sceneData of projectData.scenes)
		{
			if (sceneData.isDefaultScene)
			{
				const defaultRoot = Engine.getInstance().getSelectionRoot(sceneData.guid);
				for (const linkedSceneGuid of sceneData.linkedScenes!)
				{
					const linkedDefaultRoot = Engine.getInstance().getSelectionRoot(linkedSceneGuid);
					DefaultSceneManager.getInstance().onLinkedObjectLoaded(linkedDefaultRoot, linkedSceneGuid, defaultRoot);
				}

				for (const defaultSceneNode of sceneData.nodes)
				{
					const defaultObject = this.loadedObjects.get(defaultSceneNode.guid)!.object;
					if (defaultSceneNode.modifiedChildren || defaultObject.userData.removedSubNodes) this.loadObjectModifications(defaultObject, sceneData.guid, defaultSceneNode.modifiedChildren);
					DefaultSceneManager.getInstance().onDefaultObjectLoaded(defaultObject);
					this.loadLinkedObjects(defaultSceneNode);
				}
			}
		}

		for (const sceneData of projectData.scenes)
		{
			if (!sceneData.isDefaultScene)
			{
				for (const node of sceneData.nodes)
				{
					const object = this.loadedObjects.get(node.guid)!.object;
					if (node.modifiedChildren || object.userData.removedSubNodes) this.loadObjectModifications(object, sceneData.guid, node.modifiedChildren);
				}
			}
		}

		for (const [sceneGuid, saveRoots] of this.sceneSaveRoots.entries())
		{
			for (const saveRoot of saveRoots)
			{
				const objectData = this.loadedObjects.get(saveRoot.userData.guid)!;
				if (objectData.parent)
				{
					if (this.loadedObjects.has(objectData.parent))
					{
						this.loadedObjects.get(objectData.parent)!.object.add(saveRoot);
					}
					else
					{
						const defaultParent = DefaultSceneManager.getInstance().getDefaultObject(objectData.parent);
						const parent = DefaultSceneManager.getInstance().getLinkedObject(defaultParent!.userData.guid, sceneGuid);
						parent!.add(saveRoot);
					}

				}
				else if (objectData.parentSaveRoot)
				{
					if (this.subObjectsLookUp.has(objectData.parentSaveRoot))
					{
						const parent = this.subObjectsLookUp.get(objectData.parentSaveRoot)!.get(objectData.nodeIndex, objectData.primitiveIndex)!;
						parent.add(saveRoot);
					}
					else
					{
						const defaultParentSaveRoot = DefaultSceneManager.getInstance().getDefaultObject(objectData.parentSaveRoot)!;
						const defaultParent = this.subObjectsLookUp.get(defaultParentSaveRoot.userData.guid)!.get(objectData.nodeIndex, objectData.primitiveIndex)!;
						if(!defaultParent)
						{
							console.error(`Failed to find parent for object ${saveRoot.name} (${saveRoot.userData.guid}) in scene ${sceneGuid}. Adding to scene root instead.`);
							Engine.getInstance().getSelectionRoot(sceneGuid).add(saveRoot);
						}
						else
						{
							const parent = DefaultSceneManager.getInstance().getLinkedObject(defaultParent.userData.guid, sceneGuid)!;
							parent.add(saveRoot);
						}
					}
				}
				else
				{
					Engine.getInstance().getSelectionRoot(sceneGuid).add(saveRoot);
				}
			}
		}

		for (const loadedLinkedObjectData of this.loadedLinkedObjects)
		{
			if (loadedLinkedObjectData.defaultParent)
			{
				const parent = DefaultSceneManager.getInstance().getLinkedObject(loadedLinkedObjectData.defaultParent, loadedLinkedObjectData.sceneGuid)!;
				parent.add(loadedLinkedObjectData.object);
			}
			else if (loadedLinkedObjectData.defaultParentSaveRoot)
			{
				const defaultParent = this.subObjectsLookUp.get(loadedLinkedObjectData.defaultParentSaveRoot)!.get(loadedLinkedObjectData.nodeIndex, loadedLinkedObjectData.primitiveIndex)!;
				const parent = DefaultSceneManager.getInstance().getLinkedObject(defaultParent.userData.guid, loadedLinkedObjectData.sceneGuid)!;
				if(parent)
				{
					parent.add(loadedLinkedObjectData.object);
				}
				else
				{
					console.error(`Failed to find parent for linked object ${loadedLinkedObjectData.object.name} (${loadedLinkedObjectData.object.userData.guid}) in scene ${loadedLinkedObjectData.sceneGuid}. Adding to scene root instead.`);
					Engine.getInstance().getSelectionRoot(loadedLinkedObjectData.sceneGuid).add(loadedLinkedObjectData.object);	
				}
			}
			else
			{
				Engine.getInstance().getSelectionRoot(loadedLinkedObjectData.sceneGuid).add(loadedLinkedObjectData.object);
			}
		}

		for (const [parent, childIdentifiers] of this.childReorderMap.entries())
		{
			let changesMade = false;
			for (let i = 0; i < childIdentifiers.length; i++)
			{
				const identifiers = childIdentifiers[i];
				for (let j = 0; j < parent.children.length; j++)
				{
					const child = parent.children[j];
					if (child.userData.hideInHierarchy) continue;
					if ((identifiers.guid && child.userData.guid === identifiers.guid) ||
					(identifiers.nodeIndex !== undefined && child.userData.modelData &&
						child.userData.modelData.nodeIndex === identifiers.nodeIndex &&
					child.userData.modelData.primitiveIndex === identifiers.primitiveIndex))
					{
						if (i !== j)
						{
							parent.children.splice(i, 0, parent.children.splice(j, 1)[0]);
							changesMade = true;
						}
						break;
					}
				}
			}
			if (changesMade) ProjectSaver.getInstance().markModifiedObject(parent, SaveModificationTypes.ReorderedChildren);
		}

		for (const extrudedTextObject of this.extrudedTextObjects)
		{
			extrudedTextObject.offsetExtrudedText();
		}

		for (const [sceneGuid, camera] of this.interactiveCameras.entries())
		{
			if(camera)
			{
				camera.setCameraIsInteractiveMode(true);
				if(DefaultSceneManager.getInstance().isDefaultObject(camera.userData.guid))
				{
					const linkedScenes = DefaultSceneManager.getInstance().getLinkedScenes(sceneGuid);
					for(const linkedScene of linkedScenes)
					{
						if(this.interactiveCameras.has(linkedScene.userData.guid)) continue;

						const linkedCamera = DefaultSceneManager.getInstance().getLinkedObject(camera.userData.guid, linkedScene.userData.guid);
						if(linkedCamera && !linkedCamera.userData.saveModifications.includes(SaveModificationTypes.Camera))
						{
							(linkedCamera as ScopeCamera).setCameraIsInteractiveMode(true);
						}
					}
				}
			}
		}

		this.sceneSaveRoots.clear();
		this.loadedObjects.clear();
		this.childReorderMap.clear();
		this.interactiveCameras.clear();
		this.subObjectsLookUp.clear();
		this.loadedLinkedObjects.length = 0;
		this.extrudedTextObjects.length = 0;
	}

	loadNode(nodeData: INodeSaveData, sceneGuid: string)
	{
		let destObject : THREE.Object3D | undefined = undefined;

		if (nodeData.assetId)
		{
			const assetRoot = Engine.getInstance().getModelAsset(nodeData.assetId)!;
			const srcObject = SceneObjectUtils.getSubNode(assetRoot, nodeData.modelData!.nodeIndex, nodeData.modelData!.primitiveIndex, nodeData.assetId)!;
			const lookupDatabase = new TwoKeySubObjectLookupDatabase();
			destObject = SceneObjectUtils.cloneObject(srcObject, false, false, sceneGuid, undefined, lookupDatabase);
			this.subObjectsLookUp.set(nodeData.guid, lookupDatabase);
			destObject!.position.set(0, 0, 0);
			destObject!.quaternion.set(0, 0, 0, 1);
			destObject!.scale.set(1, 1, 1);
			if (nodeData.removedSubNodes && nodeData.removedSubNodes.length) destObject!.userData.removedSubNodes = nodeData.removedSubNodes;
			if (destObject!.userData.animations)
			{
			//remove the mixer that's automatically created by cloning the object. a new one will be created after destObject is assigned a guid
				Engine.getInstance().deleteAnimationMixer(destObject!);
			}
		}
		else if (nodeData.primitiveType)
		{
			const primitiveScale = nodeData.primitiveScale ?? 1.0;
			const geometry = Utils.getPrimitiveGeometry(nodeData.primitiveType, primitiveScale);
			destObject = new THREE.Mesh(geometry);
			destObject.userData.primitiveType = nodeData.primitiveType;
			destObject.userData.primitiveScale = primitiveScale;
		}
		else if (nodeData.light)
		{
			if (nodeData.light.type === LightTypes.Directional)
			{
				destObject = new ScopeLight(nodeData.light.type);
				(destObject  as ScopeLight).setLightColor(nodeData.light.color);
				(destObject  as ScopeLight).setLightIntensity(nodeData.light.intensity);
				destObject!.quaternion.set(0, 0, 0, 1);
			}
			else
			{
				destObject = new THREE.Object3D();
			}
		}
		else if (nodeData.audio)
		{
			destObject = new AudioSource(Engine.getInstance().getAudioListener());
			(destObject as AudioSource).setAudio3D(nodeData.audio.is3D);
			(destObject as AudioSource).setAudioVolume(nodeData.audio.volume);
			(destObject as AudioSource).setAudioClip(nodeData.audio.clip);
			(destObject as AudioSource).setAudioRange(nodeData.audio.range);
		}
		else if (nodeData.camera)
		{
			destObject = new ScopeCamera();

			if (nodeData.camera.preInteractiveBackupValues) destObject!.userData.preInteractiveBackupValues = nodeData.camera.preInteractiveBackupValues;
			if (nodeData.camera.isInteractiveModeCamera !== undefined)
			{
				if(nodeData.camera.isInteractiveModeCamera)
				{
					this.interactiveCameras.set(sceneGuid, destObject as ScopeCamera);
				}
				else
				{
					(destObject as ScopeCamera).setCameraIsInteractiveMode(false);
				}
			}
			(destObject as ScopeCamera).setCameraNearPlane(nodeData.camera.nearplane);
			(destObject as ScopeCamera).setCameraFarPlane(nodeData.camera.farPlane);
			(destObject as ScopeCamera).setCameraFov(nodeData.camera.fov);
			(destObject as ScopeCamera).setCameraOrthographicSize(nodeData.camera.orthographicSize);
			(destObject as ScopeCamera).setCameraAspect(nodeData.camera.aspect);
			(destObject as ScopeCamera).setCameraProjectionMode(nodeData.camera.projection);
			(destObject as ScopeCamera).onCameraAddedToScene(sceneGuid);
			(destObject as ScopeCamera).setCameraFocusPoint(nodeData.camera.focusPoint);
		}
		else if (nodeData.text3d)
		{
			destObject = new Text3D();
			destObject.userData.text3d = nodeData.text3d;
			(destObject as Text3D).setTextColor(destObject.userData.text3d.color);
			(destObject as Text3D).setTextAnchor(destObject.userData.text3d.anchor);
			(destObject as Text3D).generateTextGeometry();
			if (this.version <= 2.0) this.extrudedTextObjects.push((destObject as Text3D));
		}
		else if (nodeData.mediaPanel)
		{
			destObject = new MediaPanel({ aspect: nodeData.mediaPanel.aspect, scale: nodeData.mediaPanel.scale ?? 0.1 });
		}
		else
		{
			destObject = new THREE.Object3D();
		}

		destObject!.userData.visibility = nodeData.visibility;
		destObject!.userData.guid = nodeData.guid;
		destObject!.name = nodeData.name;

		if (destObject!.userData.animations)
		{
			Engine.getInstance().createAnimationMixer(destObject!);
		}

		if (nodeData.position)
		{
			destObject!.position.fromArray(nodeData.position);
			destObject!.rotation.fromArray(nodeData.rotation);
			destObject!.scale.fromArray(nodeData.scale);
		}
		else if ((nodeData as any).matrix) //older format backwards comptability
		{
			const matrix = new THREE.Matrix4().fromArray((nodeData as any).matrix);
			destObject!.applyMatrix4(matrix);
			//local matrices with zero scale would mess up rotation in the old format. resetting it to none-Nan value in such cases
			if (isNaN(destObject!.rotation.x) || isNaN(destObject!.rotation.y) || isNaN(destObject!.rotation.z))
			{
				destObject!.quaternion.set(0, 0, 0, 1);
			}
		}

		if (nodeData.materialAssetId)
		{
			const materialAsset = MaterialAsset.getMaterialAssetByGuid(nodeData.materialAssetId);
			if (materialAsset) MaterialAsset.assignMaterialAssetToObject(materialAsset, destObject as THREE.Mesh);
		}
		else if(nodeData.primitiveType || nodeData.mediaPanel)
		{ //https://scopearcloud.atlassian.net/browse/WC20-2997
			MaterialAsset.assignMaterialAssetToObject(MaterialAsset.defaultMaterial, destObject as THREE.Mesh);
		}


		if (nodeData.annotation) Callout.loadCallout(destObject!, nodeData.annotation);
		if (nodeData.pivot) TransformUtils.loadPivot(destObject!, nodeData.pivot.relativeOffset, nodeData.pivot.geometryOffset, nodeData.pivot.space);
		if (nodeData.triggers)
		{
			destObject!.userData.triggers = new Array<Trigger>();
			for (const trigger of nodeData.triggers)
			{
				if(trigger.scenarioId) trigger.scenarioId = Number(trigger.scenarioId);
				destObject!.userData.triggers.push({ ...JSON.parse(JSON.stringify(defaultTriggerObject)), ...trigger });
			}
		}

		if (nodeData.billboard)
		{
			destObject!.userData.billboard = nodeData.billboard;
			if (destObject!.userData.billboard.enabled) Engine.getInstance().getBillboardManager().enableBillboard(destObject!, sceneGuid);
		}

		if (nodeData.groups)
		{
			for (const groupGuid of nodeData.groups)
			{
				GroupsManager.addToGroup(groupGuid, [destObject!], sceneGuid);
			}
		}

		if (nodeData.unlinkedDefaultObjectGuid)
		{
			destObject!.userData.unlinkedDefaultObjectGuid = nodeData.unlinkedDefaultObjectGuid;
		}

		if (nodeData.reorderedChildrenIdentifiers)
		{
			this.childReorderMap.set(destObject!, nodeData.reorderedChildrenIdentifiers);
		}

		if(nodeData.metaData)
		{
			for(const [key, property] of Object.entries(nodeData.metaData))
			{
				MetadataManager.addCustomKey(destObject!, key, property.value);
			}
		}

		if (nodeData.partsCatalog)
		{
			destObject!.userData.partsCatalog = nodeData.partsCatalog;
		}

		MaterialAsset.registerCustomMaterialReference(destObject!);
		SceneObjectUtils.initialiseObjectProperties(destObject!);
		ProjectSaver.getInstance().markAsSaveRoot(destObject!);
		this.sceneSaveRoots.get(sceneGuid)!.push(destObject!);
		this.loadedObjects.set(destObject!.userData.guid, {
			object: destObject!,
			...(nodeData.parent && { parent: nodeData.parent }),
			...(nodeData.parentSaveRoot && { parentSaveRoot: nodeData.parentSaveRoot, nodeIndex: nodeData.nodeIndex, primitiveIndex: nodeData.primitiveIndex }),
		});
	}

	loadObjectModifications(rootObject: THREE.Object3D, sceneGuid: string, modifiedChildrenData?: IModifiedChildrenData, isLinkedObject = false)
	{
		if (rootObject.userData.removedSubNodes && !isLinkedObject)
		{
			for (let i = rootObject.userData.removedSubNodes.length - 1; i >= 0; i--)
			{
				const removedSubnodeIdentifiers = rootObject.userData.removedSubNodes[i];
				const subobjectLookup = this.subObjectsLookUp.get(rootObject.userData.guid);
				if (subobjectLookup && subobjectLookup.has(removedSubnodeIdentifiers.nodeIndex, removedSubnodeIdentifiers.primitiveIndex))
				{
					const objectToDelete = subobjectLookup.get(removedSubnodeIdentifiers.nodeIndex, removedSubnodeIdentifiers.primitiveIndex)!;
					objectToDelete.traverse((child) =>
					{
						if (child.userData.modelData)
						{
							subobjectLookup.delete(child.userData.modelData.nodeIndex, child.userData.modelData.primitiveIndex);
						}
					});
					objectToDelete.removeFromParent();
					SceneObjectUtils.disposeObject(objectToDelete, false);
				}
				else
				{
					rootObject.userData.removedSubNodes.splice(i, 1);
				}
			}
		}

		if (!modifiedChildrenData) return;
		for (const [childGuid, changeData] of Object.entries(modifiedChildrenData))
		{
			let modifiedObject: THREE.Object3D | null = rootObject;
			if (changeData.nodeIndex !== undefined)
			{
				if (!isLinkedObject)
				{
					modifiedObject = this.subObjectsLookUp.get(rootObject.userData.guid)?.get(changeData.nodeIndex, changeData.primitiveIndex) ?? null;
				}
				else
				{
					const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(rootObject.userData.guid)!;
					const defaultModifiedObject = this.subObjectsLookUp.get(defaultObject.userData.guid)?.get(changeData.nodeIndex, changeData.primitiveIndex);
					if (!defaultModifiedObject) continue;
					modifiedObject = DefaultSceneManager.getInstance().getLinkedObject(defaultModifiedObject.userData.guid, sceneGuid);
				}

				if (!modifiedObject) continue; //sub-object has changes registered to its name, but it must have been removed via its parent's removedSubNodes
			}

			if (changeData.removedSubNodes)
			{
				for (let i = changeData.removedSubNodes.length - 1; i >= 0; i--)
				{
					assert(isLinkedObject);//only a linked object would have its removedSudnodes listed in this manner
					const removedSubnodeIdentifiers = changeData.removedSubNodes[i];
					const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(modifiedObject.userData.guid)!;

					if (this.subObjectsLookUp.get(defaultObject.userData.guid)?.has(removedSubnodeIdentifiers.nodeIndex, removedSubnodeIdentifiers.primitiveIndex))
					{
						const defaultObjectToDelete = this.subObjectsLookUp.get(defaultObject.userData.guid)!.get(removedSubnodeIdentifiers.nodeIndex, removedSubnodeIdentifiers.primitiveIndex)!;
						const objectToDelete = DefaultSceneManager.getInstance().getLinkedObject(defaultObjectToDelete.userData.guid, sceneGuid);
						if (objectToDelete && objectToDelete !== modifiedObject)
						{
							objectToDelete.removeFromParent();
							DefaultSceneManager.getInstance().onLoadedObjectDisposed(objectToDelete);
							SceneObjectUtils.disposeObject(objectToDelete, false);
							if (!modifiedObject.userData.removedSubNodes) modifiedObject.userData.removedSubNodes = [];
							modifiedObject.userData.removedSubNodes.push({ nodeIndex: removedSubnodeIdentifiers.nodeIndex, primitiveIndex: removedSubnodeIdentifiers.primitiveIndex });
						}
					}
				}
			}

			if (changeData.maintainGuid)
			{
			//about to manually assign this object with its original guid.
			//need to handle all systems that are currently refering to its current guid that has been automatically generated
				if (modifiedObject.userData.animations)
				{
					Engine.getInstance().deleteAnimationMixer(modifiedObject);
				}

				if (Callout.getCallout(modifiedObject.userData.guid))
				{
					const callout = Callout.getCallout(modifiedObject.userData.guid);
					Callout.setCallout(modifiedObject.userData.guid, undefined);
					Callout.setCallout(childGuid, callout);
				}

				MaterialAsset.onObjectGuidReplaced(modifiedObject.userData.guid, childGuid);
				DefaultSceneManager.getInstance().onObjectGuidReplaced(modifiedObject.userData.guid, childGuid);

				modifiedObject.userData.guid = childGuid;
				if (modifiedObject.userData.animations)
				{
					Engine.getInstance().createAnimationMixer(modifiedObject);
				}

				ProjectSaver.getInstance().markModifiedObject(modifiedObject, SaveModificationTypes.Reference);
			}

			if (changeData.modifications)
			{
				for (const modification of changeData.modifications)
				{
					switch (modification.type)
					{
						case SaveModificationTypes.Position:
							if (MathUtils.areVectorsEqual(modifiedObject.position, { x: modification.value[0], y: modification.value[1], z: modification.value[2] })) continue;
							modifiedObject.position.fromArray(modification.value);
							break;
						case SaveModificationTypes.Rotation:
							if (MathUtils.areQuaternionsEqual(modifiedObject.quaternion, { x: modification.value[0], y: modification.value[1], z: modification.value[2], w: modification.value[3] })) continue;
							modifiedObject.quaternion.fromArray(modification.value);
							break;
						case SaveModificationTypes.Scale:
							if (MathUtils.areVectorsEqual(modifiedObject.scale, { x: modification.value[0], y: modification.value[1], z: modification.value[2] })) continue;
							modifiedObject.scale.fromArray(modification.value);
							break;
						case SaveModificationTypes.Name:
							if (modifiedObject.name === modification.value) continue;
							modifiedObject.name = modification.value;
							break;
						case SaveModificationTypes.Visibility:
							modifiedObject.userData.visibility = modification.value;
							MaterialAsset.registerCustomMaterialReference(modifiedObject);
							break;
						case SaveModificationTypes.Material:
							const current = MaterialAsset.getMaterialAssetByObjectGuid(modifiedObject.userData.guid);
							const materialAsset = MaterialAsset.getMaterialAssetByGuid(modification.value);
							if (current === materialAsset || !materialAsset) continue;
							MaterialAsset.assignMaterialAssetToObject(materialAsset, modifiedObject as THREE.Mesh);
							break;
						case SaveModificationTypes.Groups:
							if (!modifiedObject.userData.groups && modification.value.length)
							{
								for (const groupGuid of modification.value)
								{
									GroupsManager.addToGroup(groupGuid, [modifiedObject], sceneGuid);
								}
							}
							else
							{
								const additions = modification.value.filter((group: any) => !modifiedObject!.userData.groups.includes(group));
								const removals = modifiedObject.userData.groups ? modifiedObject.userData.groups.filter((group: any) => !modification.value.includes(group)) : [];
								if (!additions.length && !removals.length) continue;
								for (const groupGuid of additions) GroupsManager.addToGroup(groupGuid, [modifiedObject], sceneGuid);
								for (const groupGuid of removals) GroupsManager.removeFromGroup(groupGuid, [modifiedObject], sceneGuid);
							}
							break;
						case SaveModificationTypes.Text3D:
							assert(modifiedObject instanceof Text3D);
							(modifiedObject as Text3D).setTextColor(modification.value.color);
							(modifiedObject as Text3D).setText(modification.value.text);
							(modifiedObject as Text3D).setTextAnchor(modification.value.anchor);
							(modifiedObject as Text3D).generateTextGeometry();
							break;
						case SaveModificationTypes.Camera:
							assert(modifiedObject instanceof ScopeCamera);
							if (modification.value.preInteractiveBackupValues) modifiedObject.userData.preInteractiveBackupValues = modification.value.preInteractiveBackupValues;
							if (modification.value.isInteractiveModeCamera !== undefined)
							{
								if (modification.value.isInteractiveModeCamera)
								{
									this.interactiveCameras.set(sceneGuid, modifiedObject as ScopeCamera);
								}
								else
								{
									(modifiedObject as ScopeCamera).setCameraIsInteractiveMode(false);
								}
							}
							(modifiedObject as ScopeCamera).setCameraNearPlane(modification.value.nearplane);
							(modifiedObject as ScopeCamera).setCameraFarPlane(modification.value.farPlane);
							(modifiedObject as ScopeCamera).setCameraFov(modification.value.fov);
							(modifiedObject as ScopeCamera).setCameraOrthographicSize(modification.value.orthographicSize);
							(modifiedObject as ScopeCamera).setCameraAspect(modification.value.aspect);
							(modifiedObject as ScopeCamera).setCameraProjectionMode(modification.value.projection);
							(modifiedObject as ScopeCamera).setCameraFocusPoint(modification.value.focusPoint);
							break;
						case SaveModificationTypes.Light:
							assert(modifiedObject instanceof ScopeLight);
							(modifiedObject as ScopeLight).setLightColor(modification.value.color);
							(modifiedObject as ScopeLight).setLightIntensity(modification.value.intensity);
							break;
						case SaveModificationTypes.Audio:
							assert(modifiedObject instanceof AudioSource);
							(modifiedObject as AudioSource).setAudio3D(modification.value.is3D);
							(modifiedObject as AudioSource).setAudioVolume(modification.value.volume);
							(modifiedObject as AudioSource).setAudioClip(modification.value.clip);
							(modifiedObject as AudioSource).setAudioRange(modification.value.range);
							break;
						case SaveModificationTypes.Callout:
							if(!modification.value) continue;
							Callout.loadCallout(modifiedObject, modification.value);
							break;
						case SaveModificationTypes.Trigger:
							if(!modification.value) continue;
							if(isLinkedObject)
							{
								//WC20-3167 fix a state where a linked object contains more defaultSynced triggers than its default object
								const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(modifiedObject.userData.guid);
								if (defaultObject)
								{
									const numDefaultTriggers = defaultObject.userData.triggers ? defaultObject.userData.triggers.length : 0;
									let defaultSyncedTriggerTriggersCounter = 0;
									for (const trigger of modification.value)
									{
										if(trigger.defaultSyncedTrigger) defaultSyncedTriggerTriggersCounter++;
									}

									if(defaultSyncedTriggerTriggersCounter > numDefaultTriggers)
									{
										const numExcessTriggers = defaultSyncedTriggerTriggersCounter - numDefaultTriggers;
										modification.value.sort((a: Trigger, b: Trigger) =>
										{
											if (a.removedSyncedTrigger) return -1;
											if (b.removedSyncedTrigger) return 1;
											return 0;
										});

										for(let i = 0 ; i < numExcessTriggers; i++)
										{
											if(modification.value[i].removedSyncedTrigger)
											{
												modification.value[i] = null;
											}
											else
											{
												delete modification.value[i].defaultSyncedTrigger;
											}
										}
									}
								}
							}
							modifiedObject.userData.triggers = new Array<Trigger>();
							for (const trigger of modification.value)
							{
								if(trigger)
								{
									if(trigger.scenarioId) trigger.scenarioId = Number(trigger.scenarioId);
									modifiedObject.userData.triggers.push({ ...JSON.parse(JSON.stringify(defaultTriggerObject)), ...trigger });
								}
							}
							MaterialAsset.registerCustomMaterialReference(modifiedObject);
							break;
						case SaveModificationTypes.Billboard:
							if(!modification.value) continue;
							if (modifiedObject.userData.billboard)
							{
								const wasEnabled = modifiedObject.userData.billboard.enabled;
								modifiedObject.userData.billboard = modification.value;
								if (wasEnabled && !modification.value.enabled)
								{
									Engine.getInstance().getBillboardManager().disableBillboard(modifiedObject, sceneGuid);
								}
								else if (!wasEnabled && modification.value.enabled)
								{
									Engine.getInstance().getBillboardManager().enableBillboard(modifiedObject, sceneGuid);
								}
							}
							else
							{
								modifiedObject.userData.billboard = modification.value;
								if (modifiedObject.userData.billboard.enabled)
								{
									Engine.getInstance().getBillboardManager().enableBillboard(modifiedObject, sceneGuid);
								}
							}
							break;
						case SaveModificationTypes.Pivot:
							if (!modifiedObject.userData.pivot)
							{
								TransformUtils.loadPivot(modifiedObject, modification.value.relativeOffset, modification.value.geometryOffset, modification.value.space);
							}
							else
							{
								modifiedObject.userData.pivot.space = PivotSpace.Relative;
								TransformUtils.applyPivot(modifiedObject, modification.value.relativeOffset);
								const displayOffset = TransformUtils.getConvertedPivotOffsetSpace(modifiedObject, modifiedObject.userData.pivot.displayOffset, modification.value.relativeOffset);
								if(MathUtils.areVectorsEqual(modifiedObject.userData.pivot.displayOffset, displayOffset) && modifiedObject.userData.pivot.space === modification.value.space) continue;
								modifiedObject.userData.pivot.displayOffset.x = displayOffset.x;
								modifiedObject.userData.pivot.displayOffset.y = displayOffset.y;
								modifiedObject.userData.pivot.displayOffset.z = displayOffset.z;
								modifiedObject.userData.pivot.space = modification.value.space;
							}
							break;
						case SaveModificationTypes.Metadata:
							for(const [key, property] of Object.entries( modification.value as IMetaDataObject))
							{
								MetadataManager.addCustomKey(modifiedObject, key, property.value);
							}
							break;
						case SaveModificationTypes.PartsCatalog:
							modifiedObject.userData.partsCatalog = modification.value;
							break;
						case SaveModificationTypes.ReorderedChildren:
							this.childReorderMap.set(modifiedObject, modification.value);
							continue;
						default:
							assert(false);
							break;
					}
					//TODO: if the modification didn't actually apply a change (i.e a linekd object with value identical to its default object) - don't register it
					ProjectSaver.getInstance().markModifiedObject(modifiedObject, modification.type);
				}
			}
		}
	}

	loadLinkedObjects(defaultNodeData : INodeSaveData)
	{
		const defaultObject = this.loadedObjects.get(defaultNodeData.guid)!.object;
		assert(defaultObject);
		if (defaultNodeData.defaultObject?.links)
		{
			for (const objectLink of defaultNodeData.defaultObject.links)
			{
				const linkedObject = SceneObjectUtils.cloneObject(defaultObject, false, false, objectLink.sceneGuid);
				DefaultSceneManager.getInstance().onLinkedObjectLoaded(linkedObject, objectLink.sceneGuid, defaultObject);
				if (objectLink.modifiedChildren) this.loadObjectModifications(linkedObject, objectLink.sceneGuid, objectLink.modifiedChildren, true);
				if (this.version <= 2.0 && linkedObject instanceof Text3D) this.extrudedTextObjects.push(linkedObject);

				this.loadedLinkedObjects.push(
					{
						object: linkedObject,
						defaultParent: defaultNodeData.parent,
						sceneGuid: objectLink.sceneGuid,
						...(defaultNodeData.parent && { defaultParent: defaultNodeData.parent }),
						...(defaultNodeData.parentSaveRoot && { defaultParentSaveRoot: defaultNodeData.parentSaveRoot, nodeIndex: defaultNodeData.nodeIndex, primitiveIndex: defaultNodeData.primitiveIndex }),
					});
			}
		}
	}
	readProjectFile(filePath: string)
	{
		return new Promise<Blob>((resolve, reject) =>
		{
			const rawFile = new XMLHttpRequest();
			rawFile.open("GET", filePath, true);
			rawFile.responseType = 'blob';
			rawFile.onreadystatechange = () =>
			{
				if (rawFile.readyState === XMLHttpRequest.DONE)
				{
					if (rawFile.status === 0 || (rawFile.status >= 200 && rawFile.status < 400))
					{
						resolve(rawFile.response);
					}
					else
					{
						reject(new Error('Could not read project file'));
					}
				}
			};
			rawFile.onprogress = (event) =>
			{
				//console.log('downloading project ' + Math.round((event.loaded / event.total) * 100) + "%");
				this.emit(ProjectLoadingEvents.LOADING_PERCENT, event.loaded / event.total);
			};
			rawFile.send(null);
		});
	}

	static projectLoadFailed(projectId: string, error: any)
	{
		ErrorHandler.reportError(error, ErrorCategory.ProjectLoad, ErrorSeverity.Error);
		MessagePrompter.showError(
			{
				code: 20003,
				title: "Project Load Failed",
				content: <div>
					Failed to load project.<br />
					Please ensure your internet connection is stable, refresh and try again.<br />
					If the problem persists, try another recent save version or <a className="highlighted" href="https://scopear.zendesk.com/hc/en-us/requests/new" target="_blank" rel="noopener noreferrer help">contact support</a>.
					<br />
					{error && (
						<>
                    Error Details: {error.message ? error.message : error.toString()}
							<br />
						</>
					)}
				</div>,
				actions: [
					{
						label: "Ok",
						onClick: () => {window.location.reload();},
					},
					{
						label: "Load an earlier save",
						onClick: () =>
						{
							chooseSaveFile(projectId);
						},
					},
				],
			}
		);
	}

}
